/* ─────────────────────────────────────────────────────────────────────────
   Team store — fronts the /api/team System-of-Record from MongoDB.

   • `useTeam()`        → reactive roster + acting-operative id
   • `useCurrentOperative()` → reactive current operative + setRole/setOperative
   • `updateOperative(id, patch)` → PATCH /api/team/{id}, refresh store
   • `createOperative(body)`     → POST  /api/team
   • `deleteOperative(id)`       → DELETE /api/team/{id}

   The store is a tiny in-module singleton with a useSyncExternalStore-style
   subscriber list.  No Context Provider is needed — every component on the
   page that consumes `useTeam` or `useCurrentOperative` re-renders together
   the moment the database mutates.
   ─────────────────────────────────────────────────────────────────────── */

import { useEffect, useState } from "react";
import { api } from "./api";

const state = {
  operatives: [],
  acting_id: null,
  loaded: false,
  loading: false,
  error: null,
};

const subscribers = new Set();
const notify = () => { subscribers.forEach((cb) => cb()); };
const snapshot = () => ({ ...state, operatives: state.operatives });

export function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

let inflight = null;
export async function refreshTeam() {
  if (inflight) return inflight;
  state.loading = true; notify();
  inflight = api.team()
    .then((data) => {
      state.operatives = data.operatives || [];
      state.acting_id  = data.acting_id || (state.operatives[0]?.id ?? null);
      state.loaded = true;
      state.error  = null;
    })
    .catch((err) => { state.error = err; })
    .finally(() => { state.loading = false; inflight = null; notify(); });
  return inflight;
}

export async function setActingOperative(operativeId) {
  // Optimistic local update so the UI flips instantly, then persist.
  state.acting_id = operativeId;
  notify();
  try {
    await api.teamSetActing(operativeId);
  } catch (err) {
    state.error = err;
    // re-sync from the source-of-truth
    await refreshTeam();
  }
}

export async function updateOperative(operativeId, patch) {
  // Optimistic local update
  state.operatives = state.operatives.map((o) =>
    o.id === operativeId ? { ...o, ...patch } : o
  );
  notify();
  try {
    const updated = await api.teamUpdate(operativeId, patch);
    state.operatives = state.operatives.map((o) =>
      o.id === operativeId ? { ...updated } : o
    );
    notify();
    return updated;
  } catch (err) {
    state.error = err;
    await refreshTeam();
    throw err;
  }
}

export async function createOperative(body) {
  const created = await api.teamCreate(body);
  state.operatives = [...state.operatives, created];
  notify();
  return created;
}

export async function deleteOperative(operativeId) {
  await api.teamDelete(operativeId);
  state.operatives = state.operatives.filter((o) => o.id !== operativeId);
  if (state.acting_id === operativeId) state.acting_id = state.operatives[0]?.id ?? null;
  notify();
}

function useStoreSlice(selector) {
  const [, force] = useState(0);
  useEffect(() => {
    const unsub = subscribe(() => force((x) => x + 1));
    if (!state.loaded && !state.loading) refreshTeam();
    return unsub;
  }, []);
  return selector(snapshot());
}

export function useTeam() {
  return useStoreSlice((s) => ({
    operatives: s.operatives,
    acting_id:  s.acting_id,
    loaded:     s.loaded,
    loading:    s.loading,
    error:      s.error,
  }));
}

export function useCurrentOperative() {
  const operative = useStoreSlice((s) =>
    s.operatives.find((o) => o.id === s.acting_id) || s.operatives[0] || FALLBACK_OPERATIVE
  );
  return {
    operative,
    setOperative: async (next) => {
      const resolved = typeof next === "function" ? next(operative) : next;
      if (!resolved?.id || resolved.id === "u-loading") return;
      // If only the role changed, PATCH; if the whole identity changed, switch acting.
      if (resolved.id !== operative.id) {
        await setActingOperative(resolved.id);
      } else if (resolved.role !== operative.role) {
        await updateOperative(resolved.id, { role: resolved.role });
      }
    },
    setRole: async (roleId) => {
      if (!operative?.id || operative.id === "u-loading" || operative.role === roleId) return;
      await updateOperative(operative.id, { role: roleId });
    },
    switchActing: setActingOperative,
  };
}

// Renders before the first fetch resolves — keeps the UI alive on cold-load.
const FALLBACK_OPERATIVE = {
  id: "u-loading",
  name: "Loading…",
  email: "",
  role: "DIRECTOR",
  segments: [],
};
